import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  FileCheck,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Shield,
  Mail,
  FileText,
  Search,
  Eye,
  Award,
  Globe,
  TrendingUp,
  Lock,
  ArrowRight,
  Calendar,
  List,
  ClipboardCheck,
  Target,
  PenTool,
  BookText,
  FileSignature,
  UserCheck,
  Scale,
  Heart,
  UserCircle,
  FileSearch,
  Microscope,
  Dna,
  TestTube,
  Leaf,
  FlaskConical,
  Stethoscope,
  Map,
  GraduationCap,
  ExternalLink
} from 'lucide-react';

const ProcesoEditorial = () => {
  const [activeSection, setActiveSection] = useState('sobre-cenid');
  const [isTocOpen, setIsTocOpen] = useState(false);

  const tableOfContents = [
    { id: 'sobre-cenid', title: 'Sobre Editorial CENID', icon: BookOpen },
    { id: 'departamentos', title: 'Departamentos', icon: Users },
    { id: 'metodologia', title: 'Metodología para Publicar', icon: TrendingUp },
    { id: 'plagio', title: 'Control de Plagio', icon: Shield },
    { id: 'proceso-pares', title: 'Proceso de Evaluación', icon: Eye },
    { id: 'equipo-editorial', title: 'Equipo Editorial', icon: Users },
    { id: 'directrices-envio', title: 'Directrices para Envíos', icon: FileCheck },
    { id: 'acceso-abierto', title: 'Política de Acceso Abierto', icon: Globe },
    { id: 'autoarchivo', title: 'Política de Autoarchivo', icon: Lock },
    { id: 'tipos-contribuciones', title: 'Tipos de Contribuciones', icon: FileText },
    { id: 'preparacion', title: 'Preparación de Manuscritos', icon: PenTool },
    { id: 'organizacion', title: 'Organización de Manuscritos', icon: List },
    { id: 'comprobacion', title: 'Lista de Comprobación', icon: ClipboardCheck },
    { id: 'evaluacion', title: 'Formato de Evaluación', icon: Target },
    { id: 'etica', title: 'Ética de la Investigación', icon: Heart },
    { id: 'revisores', title: 'Comité Evaluador', icon: GraduationCap },
    { id: 'copyright', title: 'Nota de Copyright', icon: FileSignature },
    { id: 'privacidad', title: 'Deslinde y Privacidad', icon: Scale },
    { id: 'contacto', title: 'Contacto', icon: Mail }
  ];

  // ScrollSpy effect - detecta qué sección está visible usando Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Detecta cuando la sección está cerca del centro
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observar todas las secciones
    tableOfContents.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140; // Header height + some padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
      setIsTocOpen(false);
    }
  };

  const fase1Pasos = [
    {
      numero: '1.1',
      titulo: 'Envío del trabajo',
      descripcion: 'Hacer llegar por vía correo electrónico (redesdeproduccion@cenid.org) su trabajo en extenso.'
    },
    {
      numero: '1.2',
      titulo: 'Revisión de originalidad',
      descripcion: 'Revisión por parte del comité editorial de la originalidad del escrito. Se hará del conocimiento por correo electrónico y mediante carta membretada del resultado de esta primera fase en un plazo no mayor de 25 a 30 días naturales.'
    },
    {
      numero: '1.3',
      titulo: 'Observaciones (si aplica)',
      descripcion: 'En los casos en el que el comité editorial encuentre alguna duplicidad en el trabajo en extenso, se les hará llegar por escrito las observaciones encontradas y se le otorgarán 10 días naturales para volver a enviar el trabajo en extenso con las correcciones solicitadas.'
    },
    {
      numero: '1.4',
      titulo: 'Avance a Fase 2',
      descripcion: 'En los casos en el que el comité editorial no encuentre observación alguna al trabajo en extenso se procederá con la fase 2.'
    }
  ];

  const fase2Pasos = [
    {
      numero: '2.1',
      titulo: 'Evaluación por pares',
      descripcion: 'Una vez que el autor(es) tiene conocimiento del resultado de la fase 1 se dará inicio al proceso de evaluación por pares, mismo que no deberá de exceder los 150 a 240 días naturales para que el comité editorial reciba las observaciones que los pares hagan a cada artículo.'
    },
    {
      numero: '2.2',
      titulo: 'Respuesta a observaciones',
      descripcion: 'El autor(es) tiene la obligación de dar respuesta a cada una de las observaciones en un plazo que no excedan los 15 días hábiles una vez que se haga llegar por correo electrónico las recomendaciones que el comité evaluador emita y se tendrá hasta dos oportunidades para cubrir las observaciones que haga el comité evaluador.'
    },
    {
      numero: '2.3',
      titulo: 'Publicación',
      descripcion: 'Una vez que fueron subsanadas las observaciones, el autor(es) tiene 10 días hábiles a partir de esta fecha para cubrir la cuota de publicación. Una vez que se haga llegar la ficha escaneada vía correo electrónico, se procede por parte del equipo editorial en un plazo de 30 días a realizar las revisiones ortográficas de estilo, diagramación y publicación.'
    }
  ];

  const departamentos = [
    {
      nombre: 'Consejo Asesor',
      descripcion: 'Estará constituido por el Equipo Editorial y los Editores Asociados de la revista. Su función principal es fijar la política y estrategias de la revista a propuesta del Director/Editor.',
      icon: Users
    },
    {
      nombre: 'Director/Editor',
      descripcion: 'Persona de valía universitaria, reconocimiento internacional y proyección iberoamericana en el ámbito de la educación.',
      icon: Award
    },
    {
      nombre: 'Consejo Editorial',
      descripcion: 'Formado por relevantes estudiosos e investigadores, expertos en los ámbitos propios de la revista.',
      icon: BookOpen
    },
    {
      nombre: 'Editores Asociados y Consejo de Redacción',
      descripcion: 'Formado por expertos en formatos educativos. Colaborarán en la gestión y edición de la revista.',
      icon: FileText
    },
    {
      nombre: 'Evaluadores/Revisores Externos',
      descripcion: 'El Consejo Editorial se verá reforzado en su tarea por expertos evaluadores externos.',
      icon: Search
    },
    {
      nombre: 'Secretaría Técnica',
      descripcion: 'Coordinará todo el flujo de relaciones que se generan en torno a la revista.',
      icon: Mail
    }
  ];

  const tiposContribuciones = [
    {
      tipo: 'Artículo',
      descripcion: 'Resultado de una investigación completa y original. El texto se recomienda que tenga un máximo de 30 cuartillas, incluyendo tablas, figuras y referencias.',
      extension: 'Máximo 30 cuartillas'
    },
    {
      tipo: 'Nota',
      descripcion: 'Reseña de aportaciones novedosas basadas en debates actuales presentadas en congresos, foros, seminarios, reuniones académicas y políticas, tanto nacionales como internacionales.',
      extension: 'Máximo 10 cuartillas'
    },
    {
      tipo: 'Ensayo',
      descripcion: 'Aportación crítica, analítica y documentada del estado actual de conocimiento sobre un tema. Debe contener aportes novedosos, inéditos e interpretaciones personales claramente diferenciadas.',
      extension: 'Máximo 20 cuartillas'
    },
    {
      tipo: 'Trabajos de divulgación',
      descripcion: 'Trabajos que expliquen los conceptos y resultados obtenidos de descubrimientos científicos a problemáticas de actualidad por la comunidad científica de manera amena y entendible para la población general.',
      extension: 'Variable'
    }
  ];

  const preparacionPasos = [
    {
      numero: 1,
      titulo: 'Primera página',
      items: [
        'Título del manuscrito en español e inglés',
        'Nombre completo de cada autor',
        'Filiación institucional(es) de cada autor sin abreviaciones o acrónimos',
        'Información de correo electrónico de cada autor y ORCID ID',
        'Título corto de no más de 20 palabras'
      ]
    },
    {
      numero: 2,
      titulo: 'Resumen y palabras clave',
      items: [
        'Resumen escrito en tiempo pasado, tercera persona, sin exceder 250 palabras',
        'Debe reflejar completamente el contenido del manuscrito',
        'Para investigaciones: Introducción, Objetivo, Método, Resultados y Conclusiones',
        'Hasta cinco palabras clave (español e inglés) que ayuden para la indización'
      ]
    },
    {
      numero: 3,
      titulo: 'Texto principal',
      items: [
        'Secciones claramente marcadas con encabezados',
        'Secciones: Introducción, Método, Resultados, Discusión y Conclusiones',
        'Incluir en la Discusión las limitaciones del estudio, fortalezas y debilidades'
      ]
    },
    {
      numero: 4,
      titulo: 'Tablas y figuras',
      items: [
        'Las tablas deben crearse en Word (función de tabla), a renglón cerrado',
        'Ecuaciones con herramientas de Word (no imágenes)',
        'Todas las fotografías, gráficas, esquemas y diagramas como Figuras',
        'Formato .png, numeración consecutiva con números arábigos'
      ]
    },
    {
      numero: 5,
      titulo: 'Referencias',
      items: [
        'Estilo APA sexta edición',
        'Sin numeración, en estricto orden alfabético',
        'Sin separaciones por tipo de fuente consultada',
        'Mínimo 15 fuentes',
        'Fuentes inéditas y comunicaciones personales en el texto entre paréntesis'
      ]
    },
    {
      numero: 6,
      titulo: 'Seguimiento',
      items: [
        'Mantener copia de la versión final del manuscrito',
        'Recibirá correo electrónico como acuse de recibo',
        'Se le mantendrá informado del proceso y decisión final'
      ]
    },
    {
      numero: 7,
      titulo: 'Formato del documento',
      items: [
        'Manuscrito a 1.5 líneas, justificado',
        'Fuente Times New Roman de 12 puntos',
        'Márgenes de por lo menos 2.5 cm en tamaño carta',
        'Evitar abreviaturas no convencionales',
        'Unidades científicas en Sistema Internacional de Unidades',
        'Eliminar campos de automatización de referencias',
        'Desactivar "control de cambios" del procesador'
      ]
    },
    {
      numero: 8,
      titulo: 'Símbolos',
      items: [
        'Utilizar función "Insertar" del procesador de textos',
        'Pegar el carácter Unicode apropiado'
      ]
    },
    {
      numero: 9,
      titulo: 'Niveles de encabezado',
      items: [
        'Limitar secciones y sub-secciones a 3 niveles',
        'Asegurar que los niveles estén claramente indicados en el texto'
      ]
    },
    {
      numero: 10,
      titulo: 'Abreviaturas',
      items: [
        'Definir abreviaturas en la primera aparición en el texto',
        'No utilizar abreviaturas no estándar a menos que aparezcan 3 veces',
        'Mantener las abreviaturas al mínimo'
      ]
    }
  ];

  const organizacionSecciones = [
    {
      categoria: 'Sección de Inicio',
      secciones: [
        { nombre: 'Página de título', descripcion: 'Título del manuscrito, autores, institución de afiliación, correo electrónico' },
        { nombre: 'Resumen y Palabras clave', descripcion: 'Máximo 250 palabras y hasta 5 palabras clave' },
        { nombre: 'Introducción', descripcion: 'Contexto, antecedentes y objetivos del estudio' }
      ]
    },
    {
      categoria: 'Sección Intermedia',
      secciones: [
        { nombre: 'Materiales y métodos', descripcion: 'Descripción clara y completa de la metodología empleada' },
        { nombre: 'Resultados', descripcion: 'Presentación ordenada y clara de los hallazgos' },
        { nombre: 'Discusión', descripcion: 'Interpretación y contexto de los resultados' },
        { nombre: 'Conclusiones', descripcion: 'Aportaciones al conocimiento producto de la investigación' }
      ]
    },
    {
      categoria: 'Sección Final',
      secciones: [
        { nombre: 'Agradecimientos', descripcion: 'Reconocimientos a colaboradores y financiadores' },
        { nombre: 'Referencias', descripcion: 'Listado de fuentes consultadas en formato APA' },
        { nombre: 'Información de soporte', descripcion: 'Anexos si se requiere' }
      ]
    }
  ];

  const formatoEvaluacion = [
    {
      seccion: 'Título',
      descripcion: 'Verificar que:',
      criterios: [
        'Describa adecuadamente el contenido esencial de la contribución',
        'No tenga más de 20 palabras'
      ]
    },
    {
      seccion: 'Palabras clave',
      descripcion: 'Asegurarse de que:',
      criterios: [
        'Sean indicativas del contenido de la contribución, e incluyan un máximo de seis términos (simples o compuestos)',
        'Sean útiles para identificar lo esencial de una contribución'
      ]
    },
    {
      seccion: 'Resumen',
      descripcion: 'Cotejar que:',
      criterios: [
        'Tenga un máximo de 250 palabras, sin contar preposiciones o artículos',
        'Se presente en máximo dos párrafos',
        'Incluya los elementos más importantes del trabajo: objetivos, métodos y resultados. Los resultados deben constituir por lo menos 50% del Resumen, destacando los más relevantes',
        'Sea claro y conciso, pues frecuentemente el Resumen es lo único que se lee al consultar y citar una publicación',
        'Incluya los nombres científicos de cada una de las especies mencionadas'
      ],
      nota: 'Se recomienda al árbitro revisar el Resumen cuando ya haya hecho lo propio con los capítulos Resultados y Discusión, y Conclusiones.'
    },
    {
      seccion: 'Introducción',
      descripcion: 'Comprobar que:',
      criterios: [
        'Contenga los antecedentes y justificación específicos del tema, expuestos en forma clara y ordenada; apoyada con referencias bibliográficas apropiadas',
        'Los objetivos y las hipótesis estén claramente indicados',
        'El mayor número de referencias sean de publicación reciente (10 años o menos)',
        'Las referencias aludan específicamente al tema, eliminando las que a su juicio sean superfluas o parezcan simular una erudición falsa',
        'La bibliografía no aparezca como una lista de fichas sin un objetivo definido, o que se use "citas múltiples" para apoyar un concepto, pues esto sólo sirve para aparentar una amplia revisión bibliográfica. Como regla general, un máximo de tres citas debe bastar para soportar una aseveración',
        'No se empleen citas difíciles o imposibles de consultar, como notas de cursos, información mimeografiada e informes técnicos, ya que no son verificables ni accesibles para la mayoría de los lectores'
      ]
    },
    {
      seccion: 'Materiales y Métodos',
      descripcion: 'Cerciorarse de que:',
      criterios: [
        'Se describan en forma clara, breve, concisa y ordenada',
        'En cada experimento o grupo de experimentos se anoten claramente los tratamientos aplicados, el diseño experimental usado y las condiciones ambientales o generales de conducción, amén de las variables evaluadas y los análisis estadísticos aplicados',
        'En la descripción de las variables se precise la forma en que se midió cada una, el instrumento usado (con marca, modelo, y empresa que lo fabrica) y sus unidades y símbolos conforme al Sistema Internacional de Unidades. Las variables deben entenderse en forma completa, sin necesidad de leer el texto, con las excepciones inevitables',
        'Las variables generadas mediante transformaciones, combinaciones o relaciones de una o más medidas directamente, también estén descritas con su ecuación y referencia bibliográfica, de ser el caso',
        'Cuando se use abreviaturas para las variables, éstas se definan en este capítulo (y no hasta el de Resultados), aunque sean de uso común en alguna disciplina científica',
        'Los materiales y métodos empleados sean concordantes con los objetivos o hipótesis planteados. La falta de correspondencia entre los métodos y los objetivos debe ser un serio motivo de rechazo',
        'La descripción minuciosa de una metodología sólo se haga cuando sea una innovación. Dado el caso, debe describirse con la amplitud suficiente para que otro investigador la pueda repetir o reproducir',
        'No contenga descripciones de protocolos de dominio común',
        'No se presenten cuadros de análisis de varianza de diseños experimentales de uso común'
      ]
    },
    {
      seccion: 'Resultados',
      descripcion: 'Asegurarse de que:',
      criterios: [
        'Se presenten en forma ordenada, clara y precisa',
        'La descripción de resultados no repita la información en cuadros o figuras',
        'Todos los datos sean legibles. No es raro encontrar letras o números demasiado pequeños, borrosos o confusos',
        'Tanto los cuadros como las figuras sean comprensibles sin necesidad de leer el texto',
        'No se presenten cuadros o figuras sobrecargados de información. Hay excelentes textos que instruyen sobre la forma de presentar cuadros y figuras en un artículo científico',
        'Los resultados sean congruentes con los objetivos y métodos descritos'
      ]
    },
    {
      seccion: 'Discusión',
      descripcion: 'El propósito básico de la discusión es explicar los resultados de su investigación, donde se presenta las limitaciones del estudio, lo que falta por realizar, los resultados similares con otros estudios. En definitiva, la discusión es la relación entre los resultados y conclusiones que presenta nuestro trabajo con los trabajos similares al nuestro, que aspectos presentan de similitud y de diferencia, donde finalmente con ello llegamos a las limitaciones y avance que hemos obtenido con nuestro estudio.',
      criterios: []
    },
    {
      seccion: 'Conclusiones',
      descripcion: 'Este capítulo es obligatorio en toda contribución, incluyendo los Ensayos, pues en él se destaca las aportaciones al conocimiento producto de la investigación. En este capítulo debe verificarse que:',
      criterios: [
        'Se omitan especulaciones o deducciones no demostradas en el texto',
        'Las conclusiones no se presenten en forma numerada',
        'No contenga referencias bibliográficas'
      ]
    },
    {
      seccion: 'Literatura Citada',
      descripcion: 'Confirmar que:',
      criterios: [
        'Se utilice las normas de acuerdo al manual de APA Sexta edición',
        'Cada referencia contenga toda la información requerida en la Guía para autores, en el orden estipulado y con la puntuación correcta. Es muy común que falte el número de páginas de los libros consultados, que se altere el orden de las iniciales en los nombres de autores, y que se omita el título del libro o el nombre del editor en el caso de capítulos de libros',
        'Las referencias aparezcan en orden alfabético y suborden cronológico',
        'Las citas listadas estén referidas en el texto, y viceversa, y que los nombres y años coincidan todas las veces que se citan'
      ]
    }
  ];

  const listaComprobacion = [
    'El archivo enviado está en formato Microsoft Word',
    'El texto cumple con los requisitos bibliográficos y de estilo indicados',
    'Se revisó que todas las secciones estén completas',
    'Las referencias están en formato APA sexta edición',
    'El resumen no excede 250 palabras',
    'Se incluyeron palabras clave en español e inglés',
    'Las figuras están en formato .png',
    'Las tablas fueron creadas con la función de tabla de Word'
  ];

  const revisores = [
    { nombre: 'Saúl Rick Fernández Hurtado', institucion: 'Universidad Santiago de Cali', pais: 'Colombia', orcid: '0000-0001-5167-7597' },
    { nombre: 'Alba Patricia Guzmán Duque', institucion: 'Unidades Tecnológicas de Santander', pais: 'Colombia', orcid: '0000-0003-1704-6884' },
    { nombre: 'Olga Francisca Salazar Blanco', institucion: 'Universidad de Antioquia', pais: 'Colombia', orcid: '0000-0003-1019-9415' },
    { nombre: 'Amador Herney Rúa Arias', institucion: 'Universidad de Antioquia', pais: 'Colombia', orcid: '0000-0001-8218-6651' },
    { nombre: 'Francisco J. Sanchez Muniz', institucion: 'Universidad Complutense de Madrid', pais: 'España', orcid: '0000-0002-2660-5126' },
    { nombre: 'Francisco Liébana Cabanillas', institucion: 'Universidad de Granada', pais: 'España', orcid: '0000-0002-3255-0651' },
    { nombre: 'Dionisio Buendia Carrillo', institucion: 'Universidad de Granada', pais: 'España', orcid: '0000-0002-8091-6443' },
    { nombre: 'José Luís Sáez Lozano', institucion: 'Universidad de Granada', pais: 'España', orcid: '0000-0002-0089-3844' },
    { nombre: 'Antonio Vásquez Hidalgo', institucion: 'Universidad de El Salvador', pais: 'El Salvador', orcid: '0000-0001-5643-8317' },
    { nombre: 'Erico Ernesto Wulf Betancourt', institucion: 'Universidad de La Serena', pais: 'Chile', orcid: '0000-0002-3863-3263' },
    { nombre: 'Armando Alberto Jipsion Pino', institucion: 'Universidad Tecnológica de Panamá', pais: 'Panamá', orcid: '0000-0002-2018-4826' },
    { nombre: 'Ana María Florez León', institucion: 'Universidad Latina de Panamá', pais: 'Panamá', orcid: '0000-0003-2060-5202' },
    { nombre: 'José Antonio dos Santos Borges', institucion: 'Universidade Federal do Rio de Janeiro', pais: 'Brasil', orcid: '0000-0002-3610-7569' },
    { nombre: 'Manuel Iván Rodríguez Borbón', institucion: 'New Mexico State University', pais: 'USA', orcid: '0000-0001-8405-4599' },
    { nombre: 'Hansuk Sohn', institucion: 'New Mexico State University', pais: 'USA', orcid: '0000-0002-8126-730X' },
    { nombre: 'Jaime Sánchez Leal', institucion: 'University of Texas at El Paso', pais: 'USA', orcid: '0000-0002-6324-2379' },
    { nombre: 'Eduardo Rafael Poblano Ojinaga', institucion: 'Tecnológico Nacional de México/IT de Ciudad Juárez', pais: 'México', orcid: '0000-0003-3482-7252' },
    { nombre: 'Adán Valles Chávez', institucion: 'Tecnológico Nacional de México/ IT de Cd. Juárez', pais: 'México', orcid: '0000-0002-6559-0123' },
    { nombre: 'Diago Adiel Sandoval Chávez', institucion: 'Tecnológico Nacional de México/I.T. de Cd. Juárez', pais: 'México', orcid: '0000-0002-2536-1844' },
    { nombre: 'Arturo González Torres', institucion: 'Tecnológico Nacional de México, Instituto Tecnológico de Milpa Alta', pais: 'México', orcid: '0000-0002-3337-7600' },
    { nombre: 'Fátima Yaraset Mendoza Montero', institucion: 'Tecnológico Nacional de México, Instituto Tecnológico de Milpa Alta', pais: 'México', orcid: '0000-0003-0240-5434' },
    { nombre: 'Vianey Ríos Romero', institucion: 'Tecnológico Nacional de México, Instituto Tecnológico de Milpa Alta', pais: 'México', orcid: '0000-0002-5328-5586' },
    { nombre: 'María Teresa Martínez Almanza', institucion: 'Universidad Autónoma de Ciudad Juárez', pais: 'México', orcid: '0000-0002-1056-9204' },
    { nombre: 'Rocío Calderón García', institucion: 'Universidad de Guadalajara', pais: 'México', orcid: '0000-0003-0716-3446' },
    { nombre: 'Concepción Angélica Mendieta Ramírez', institucion: 'Benemérita Universidad Autónoma de Puebla', pais: 'México', orcid: '0000-0001-9344-8653' },
    { nombre: 'Ulises Delgado Sánchez', institucion: 'Universidad Autónoma del Estado de Morelos', pais: 'México', orcid: '0000-0002-4318-0238' },
    { nombre: 'Beania Salcedo Moncada', institucion: 'Universidad Autónoma de Nuevo León', pais: 'México', orcid: '0000-0001-9175-8914' },
    { nombre: 'José María López Prado', institucion: 'Universidad Autónoma de Nuevo León', pais: 'México', orcid: '0000-0001-9175-8914' },
    { nombre: 'Jesús García Reyes', institucion: 'Universidad Nacional Autónoma de México', pais: 'México', orcid: '0000-0003-1145-7164' },
    { nombre: 'Guillermo Cutrera', institucion: 'Universidad Nacional del Mar del Plata', pais: 'Argentina', orcid: '0000-0001-7959-948X' },
    { nombre: 'Evangelina Gabriela Dulce', institucion: 'Universidad de Buenos Aires', pais: 'Argentina', orcid: '0000-0002-5601-1097' },
    { nombre: 'Nancy Cristina Crespo Bosmediano', institucion: 'Universidad de Las Américas', pais: 'Ecuador', orcid: '0000-0002-2398-217X' },
    { nombre: 'Noel Barengo', institucion: 'Pedro Jurado de Los Santos', pais: 'España', orcid: '0000-0001-8239-8558' },
    { nombre: 'Isabel Álvarez Casovas', institucion: 'Universidad Complutense de Madrid', pais: 'España', orcid: '0000-0003-2016-0514' },
    { nombre: 'Laura Rayón Rumayor', institucion: 'Universidad Complutense de Madrid', pais: 'España', orcid: '0000-0003-0339-8221' },
    { nombre: 'Lidia López Lozano', institucion: 'Universidad de Sevilla', pais: 'España', orcid: '0000-0002-5181-638X' },
    { nombre: 'Daniel Pablo de la Cruz Sanchez Mata', institucion: 'Universidad Complutense de Madrid', pais: 'España', orcid: '0000-0001-5274-6267' },
    { nombre: 'Diana Mercedes Revilla Figueroa', institucion: 'Pontificia Universidad Católica del Perú', pais: 'Perú', orcid: '0000-0002-6099-0064' },
    { nombre: 'Angélica Jeannette Vera Sagredo', institucion: 'Universidad Católica de la Santísima Concepción', pais: 'Chile', orcid: '0000-0003-1657-2241' },
    { nombre: 'José Rolando Silva Serrano', institucion: 'Universidad de Santiago de Chile', pais: 'Chile', orcid: '0000-0003-1451-8903' },
    { nombre: 'Ana Clara Sanz Ochotorena', institucion: 'Universidad de La Habana', pais: 'Cuba', orcid: '0000-0002-5124-3905' },
    { nombre: 'Jaime Brenes Madriz', institucion: 'Instituto Tecnológico de Costa Rica', pais: 'Costa Rica', orcid: '0000-0003-2325-8808' },
    { nombre: 'Rafael Montanari', institucion: 'Universidade Estadual Paulista', pais: 'Brasil', orcid: '0000-0002-3557-2362' },
    { nombre: 'Reinaldo Luiz Bozelli', institucion: 'Universidade Federal do Rio de Janeiro', pais: 'Brasil', orcid: '0000-0001-9916-1629' }
  ];

  const equipoEditorial = {
    direccion: [
      { nombre: 'Francisco Santillán Campos', cargo: 'Presidente - Director General y Director Editorial CENID', institucion: 'CENID', pais: 'México' },
      { nombre: 'Nancy Cristina Crespo Bosmediano', cargo: 'Directora de Publicaciones', institucion: 'Universidad de Las Américas', pais: 'Ecuador' }
    ],
    edicion: [
      { nombre: 'Orlanda Patricia Santillan Castillo', cargo: 'Editor Académico', institucion: 'Centro de Estudios e Investigaciones para el Desarrollo Docente', pais: 'México' },
      { nombre: 'Norma Alicia Santillan Castillo', cargo: 'Corrector de textos', institucion: 'Centro de Estudios e Investigaciones para el Desarrollo Docente', pais: 'México' }
    ],
    editoresAsociados: [
      { nombre: 'María Teresa Martínez Almanza', institucion: 'Universidad Autónoma de Ciudad Juárez', pais: 'México' },
      { nombre: 'Hansuk Sohn', institucion: 'New Mexico State University', pais: 'USA' },
      { nombre: 'Isabel Álvarez Casovas', institucion: 'Universidad Complutense de Madrid', pais: 'España' },
      { nombre: 'Laura Rayón Rumayor', institucion: 'Universidad Complutense de Madrid', pais: 'España' },
      { nombre: 'Diana Mercedes Revilla Figueroa', institucion: 'Pontificia Universidad Católica del Perú', pais: 'Perú' },
      { nombre: 'Lidia López Lozano', institucion: 'Universidad de Sevilla', pais: 'España' },
      { nombre: 'Angélica Jeannette Vera Sagredo', institucion: 'Universidad Católica de la Santísima Concepción', pais: 'Chile' }
    ],
    secretaria: [
      { nombre: 'Arturo González Torres', institucion: 'Tecnológico Nacional de México, Instituto Tecnológico de Milpa Alta', pais: 'México' }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link
            to="/editorial"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            Volver a Editorial
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
              <FileCheck className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Proceso Editorial
              </h1>
              <p className="text-xl text-green-100">
                Editorial CENID - Metodología para publicación académica
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        <div className="flex gap-8 relative">
          {/* Tabla de Contenidos - Desktop */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-32 bg-white rounded-lg shadow-lg border-t-4 border-[#0b742f] overflow-hidden relative">
              <div className="p-6 pb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <List className="w-6 h-6 text-[#0b742f]" />
                  Tabla de Contenidos
                </h3>
              </div>
              <nav className="space-y-1 px-6 pb-6 max-h-[calc(100vh-220px)] overflow-y-auto toc-scrollbar" style={{ scrollbarWidth: 'thin', scrollbarColor: '#0b742f #f3f4f6' }}>
                {tableOfContents.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-300 border-l-4 ${isActive
                        ? 'bg-[#0b742f] text-white shadow-md border-[#085a24] transform scale-105'
                        : 'text-gray-700 hover:bg-gray-100 border-transparent hover:border-gray-300'
                        }`}
                    >
                      <IconComponent className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                      <span className={`text-sm font-medium ${isActive ? 'font-bold' : ''}`}>{item.title}</span>
                    </button>
                  );
                })}
              </nav>
              {/* Gradiente indicador de scroll */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            </div>
          </aside>

          {/* Botón de Tabla de Contenidos - Mobile */}
          <button
            onClick={() => setIsTocOpen(!isTocOpen)}
            className="lg:hidden fixed bottom-6 right-6 z-50 bg-[#0b742f] text-white p-4 rounded-full shadow-2xl hover:bg-[#085a24] transition-all duration-200"
          >
            <List className="w-6 h-6" />
          </button>

          {/* Tabla de Contenidos - Mobile Overlay */}
          {isTocOpen && (
            <>
              <div
                className="lg:hidden fixed inset-0 bg-black/50 z-40"
                onClick={() => setIsTocOpen(false)}
              />
              <div className="lg:hidden fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-50 overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <List className="w-6 h-6 text-[#0b742f]" />
                      Contenidos
                    </h3>
                    <button
                      onClick={() => setIsTocOpen(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <ArrowRight className="w-6 h-6" />
                    </button>
                  </div>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => {
                      const IconComponent = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-300 border-l-4 ${isActive
                            ? 'bg-[#0b742f] text-white shadow-md border-[#085a24] transform scale-105'
                            : 'text-gray-700 hover:bg-gray-100 border-transparent hover:border-gray-300'
                            }`}
                        >
                          <IconComponent className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                          <span className={`text-sm font-medium ${isActive ? 'font-bold' : ''}`}>{item.title}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </>
          )}

          {/* Contenido Principal */}
          <main className="flex-1 min-w-0">
            <div className="space-y-12">

              {/* Sobre Editorial CENID */}
              <section id="sobre-cenid" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <BookOpen className="w-8 h-8 text-[#0b742f]" />
                      Sobre Editorial CENID
                    </h2>
                    <div className="prose max-w-none text-gray-700 space-y-4">
                      <p>
                        La Editorial del Centro de Estudios e Investigaciones para el Desarrollo Docente, es una editorial internacional académica, indizada, arbitrada por pares a ciegas en formato electrónico editada por el Centro de Estudios e Investigaciones para el Desarrollo Docente CENID A.C., con una orientación multidisciplinaria.
                      </p>
                      <p>
                        Su propósito es publicar textos originales universitarios científicos que se centren en la confluencia de los ámbitos temáticos propios de la investigación y desarrollo en la educación. Además, se pretende ser una plataforma para la difusión de novedades editoriales relevantes de este mismo ámbito.
                      </p>
                      <p>
                        Editorial CENID está pensada para dar difusión a trabajos de académicos, investigadores, docentes, profesionales, técnicos y estudiantes de posgrado interesados en los aspectos teóricos y prácticos de modelos e intervenciones que tengan como principal objetivo el mejoramiento de sus prácticas educativas y formativas, en instituciones educativas.
                      </p>
                      <p>
                        Está dirigida a investigadores, especialistas, profesores y estudiantes de grado y posgrado relacionados con el campo educativo. Se encuentra abierta a todos los desarrollos de las ciencias de la educación.
                      </p>
                      <p className="font-semibold text-[#0b742f]">
                        Su misión es brindar el soporte científico a la discusión y diseño de políticas públicas en el sector educación, la gestión educativa y la docencia en el aula estén basados en la evidencia que genera la investigación.
                      </p>
                      <p>
                        En Editorial CENID las directrices y las prácticas éticas constituyen una parte esencial de la cultura editorial, se fomenta y cuida las buenas prácticas éticas, así como la detección y erradicación de las malas praxis editoriales, respaldado por los Principios de Transparencia y Mejores Prácticas en Publicaciones Académicas (COPE).
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Departamentos */}
              <section id="departamentos" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Users className="w-8 h-8 text-blue-600" />
                      Departamentos de la Editorial CENID
                    </h2>
                    <p className="text-gray-700 mb-6">
                      Se edita mediante mecanismos de gestión colegiada, plural y profesional. Los órganos de gobierno de la editorial son de carácter colegiado y de tipo personal y con funciones diferenciadas.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {departamentos.map((dept, index) => {
                        const IconComponent = dept.icon;
                        return (
                          <div
                            key={index}
                            className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-200"
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-white p-2 rounded-full shadow-sm">
                                <IconComponent className="w-6 h-6 text-blue-600" />
                              </div>
                              <h3 className="font-bold text-gray-900">{dept.nombre}</h3>
                            </div>
                            <p className="text-sm text-gray-700">{dept.descripcion}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              {/* Fases del Proceso */}
              <section id="metodologia" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-[#0b742f]" />
                      Metodología para Publicar
                    </h2>

                    <div className="bg-blue-50 rounded-lg p-6 mb-8 border-l-4 border-blue-600">
                      <p className="text-gray-800">
                        Todo trabajo recibido por el Comité Editorial merecerá un inmediato acuse de recibo. El Comité Editorial considerará, en primera instancia, la presentación y el estilo del artículo. Posteriormente será sometido a un sistema de arbitraje por pares.
                      </p>
                    </div>

                    {/* Fase 1 */}
                    <div className="mb-10">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-[#0b742f] text-white px-4 py-2 rounded-lg font-bold text-lg">
                          FASE 1
                        </div>
                        <h3 className="text-2xl font-bold text-[#0b742f]">Revisión de Originalidad</h3>
                        <div className="flex items-center gap-2 text-gray-600 ml-auto">
                          <Clock className="w-5 h-5" />
                          <span className="font-semibold">25-30 días</span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {fase1Pasos.map((paso, index) => (
                          <div
                            key={index}
                            className="flex gap-4 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-[#0b742f] hover:shadow-md transition-shadow duration-200"
                          >
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-[#0b742f] text-white rounded-full flex items-center justify-center font-bold">
                                {paso.numero}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-2">{paso.titulo}</h4>
                              <p className="text-gray-700 text-sm">{paso.descripcion}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Fase 2 */}
                    <div>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-[#e87729] text-white px-4 py-2 rounded-lg font-bold text-lg">
                          FASE 2
                        </div>
                        <h3 className="text-2xl font-bold text-[#e87729]">Evaluación por Pares</h3>
                        <div className="flex items-center gap-2 text-gray-600 ml-auto">
                          <Clock className="w-5 h-5" />
                          <span className="font-semibold">150-240 días</span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {fase2Pasos.map((paso, index) => (
                          <div
                            key={index}
                            className="flex gap-4 p-5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-l-4 border-[#e87729] hover:shadow-md transition-shadow duration-200"
                          >
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-[#e87729] text-white rounded-full flex items-center justify-center font-bold">
                                {paso.numero}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-2">{paso.titulo}</h4>
                              <p className="text-gray-700 text-sm">{paso.descripcion}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Nota importante */}
                    <div className="mt-8 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-6 border-l-4 border-yellow-500">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">NOTA IMPORTANTE</h4>
                          <p className="text-gray-700">
                            Todo trabajo que se le haga llegar tiene que cubrir en su totalidad el proceso metodológico arriba señalado. No por haber cubierto el monto solicitado para su publicación significa que el autor o los autores quedan exentos de cumplir con las observaciones que se le haga a su trabajo.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Control y Revisión de Plagio */}
              <section id="plagio" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-red-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Shield className="w-8 h-8 text-red-600" />
                      Control y Revisión de Plagio
                    </h2>
                    <div className="space-y-4 text-gray-700">
                      <p>
                        Una vez recibido el artículo, y cumpliendo con las normas de publicación de la editorial, se procede a la revisión de plagio, utilizando programa especializado.
                      </p>
                      <p>
                        Si de esta revisión surge la certeza de que el contenido, ya sea en parte o en su totalidad, es copia de otro material ya publicado, se procederá a retirar el escrito en cuestión independientemente de la etapa de revisión en la que se encuentre.
                      </p>
                      <p>
                        Asimismo, el acto de retirar un texto con confirmación de plagio puede realizarse una vez ya publicado el número de manera online.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Proceso de Evaluación por Pares */}
              <section id="proceso-pares" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-purple-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Eye className="w-8 h-8 text-purple-600" />
                      Proceso de evaluación por pares
                    </h2>

                    <div className="space-y-6">
                      {/* Fase 1 */}
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-l-4 border-purple-600">
                        <div className="flex items-start gap-4">
                          <div className="bg-purple-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0">
                            1
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Revisión inicial</h3>
                            <div className="space-y-3 text-gray-700">
                              <p className="leading-relaxed">
                                Todos los artículos / contenidos recibidos son revisados, en primer lugar, que cumplan los criterios formales exigidos por la revista que viene en la sección de Envíos. Que garantizará la adecuación a las normas de la editorial, la pertinencia, el rigor y la originalidad de las investigaciones. Durante esta fase, los manuscritos serán sometidos a la <strong>herramienta anti plagio</strong>.
                              </p>
                              <div className="bg-white p-4 rounded-lg border border-purple-200">
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="w-5 h-5 text-red-600" />
                                  <p className="font-semibold text-gray-900">
                                    Los artículos que no se ajusten a los criterios formales y/o a la política editorial de la revista serán desestimados.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fase 2 */}
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-6 border-l-4 border-indigo-600">
                        <div className="flex items-start gap-4">
                          <div className="bg-indigo-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0">
                            2
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Evaluación por pares (doble ciego)</h3>
                            <div className="space-y-3 text-gray-700">
                              <p className="leading-relaxed">
                                En una segunda fase, los artículos que hayan superado las dos revisiones anteriores se turnarán al proceso de arbitraje tipo <strong>doble ciego (double-blind assessment)</strong>:
                              </p>

                              <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                  <span>Los editores <strong>no le dan a conocer a los revisores</strong> el nombre de los autores del manuscrito a revisar</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                  <span>Los editores <strong>no le dan a conocer a los autores</strong> los nombres de los árbitros</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                  <span>Los evaluadores emiten un juicio sobre las propuestas de publicación, con las observaciones que consideran pertinentes</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                  <span>Cuando la evaluación es positiva, las observaciones de los evaluadores se enviarán a los autores mediante los editores de la editorial</span>
                                </li>
                              </ul>

                              <div className="bg-white p-4 rounded-lg border border-indigo-200 mt-4">
                                <p className="text-sm">
                                  <strong>Nota:</strong> Para ser incluidos en la publicación, es necesario que los trabajos se encuentren libres de compromisos editoriales con cualquier otra publicación, revista u órgano editorial.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fase 3 */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-600">
                        <div className="flex items-start gap-4">
                          <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0">
                            3
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Decisión final</h3>
                            <div className="space-y-3 text-gray-700">
                              <p className="leading-relaxed">
                                Cada contribución será sometida a evaluación tipo doble ciego por <strong>académicos especializados en la temática</strong> que sea presentada. El fallo de los dictaminadores será <strong>inapelable</strong> y determinará la aceptación o rechazo de las propuestas.
                              </p>
                              <p className="leading-relaxed">
                                El Consejo Editorial de la revista acordará el número en que aparecerán los textos que resulten aprobados.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Responsabilidad de los revisores */}
                      <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Shield className="w-6 h-6 text-purple-600" />
                          La revisión por pares / responsabilidad de los revisores
                        </h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <Target className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Los dictámenes deben ser <strong>objetivos</strong></span>
                          </li>
                          <li className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Los revisores <strong>no deben tener ningún conflicto de intereses</strong> con respecto a la investigación, los autores y/o los financiadores de la investigación</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Los revisores deben señalar si hay algún contenido que aún no se cite en el trabajo que se somete a revisión</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Artículos revisados deben ser tratados de forma <strong>confidencial</strong></span>
                          </li>
                        </ul>
                        <div className="mt-4 bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-700">
                            El Equipo Editorial garantiza la adecuación de estos evaluadores a la temática del artículo objeto de evaluación, además de atender posibles conflictos de intereses.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Equipo Editorial */}
              <section id="equipo-editorial" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-teal-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Users className="w-8 h-8 text-teal-600" />
                      Equipo Editorial
                    </h2>

                    <div className="space-y-8">
                      {/* Dirección */}
                      <div>
                        <h3 className="text-2xl font-bold text-teal-600 mb-4 flex items-center gap-2">
                          <Award className="w-6 h-6" />
                          Dirección
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {equipoEditorial.direccion.map((miembro, index) => (
                            <div key={index} className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-5 border border-teal-200">
                              <h4 className="font-bold text-gray-900 mb-2">{miembro.nombre}</h4>
                              <p className="text-sm text-teal-700 font-semibold mb-2">{miembro.cargo}</p>
                              <div className="text-sm text-gray-700">
                                <p>{miembro.institucion}</p>
                                <p className="flex items-center gap-2 mt-1">
                                  <Globe className="w-4 h-4" />
                                  {miembro.pais}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Edición */}
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
                          <PenTool className="w-6 h-6" />
                          Edición
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {equipoEditorial.edicion.map((miembro, index) => (
                            <div key={index} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                              <h4 className="font-bold text-gray-900 mb-2">{miembro.nombre}</h4>
                              <p className="text-sm text-blue-700 font-semibold mb-2">{miembro.cargo}</p>
                              <div className="text-sm text-gray-700">
                                <p>{miembro.institucion}</p>
                                <p className="flex items-center gap-2 mt-1">
                                  <Globe className="w-4 h-4" />
                                  {miembro.pais}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Editores Asociados */}
                      <div>
                        <h3 className="text-2xl font-bold text-purple-600 mb-4 flex items-center gap-2">
                          <BookOpen className="w-6 h-6" />
                          Editores Asociados
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {equipoEditorial.editoresAsociados.map((miembro, index) => (
                            <div key={index} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
                              <h4 className="font-bold text-gray-900 mb-2 text-sm">{miembro.nombre}</h4>
                              <div className="text-xs text-gray-700">
                                <p className="leading-snug mb-1">{miembro.institucion}</p>
                                <p className="flex items-center gap-2">
                                  <Globe className="w-3 h-3" />
                                  {miembro.pais}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Secretaría */}
                      <div>
                        <h3 className="text-2xl font-bold text-green-600 mb-4 flex items-center gap-2">
                          <FileText className="w-6 h-6" />
                          Secretaría
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {equipoEditorial.secretaria.map((miembro, index) => (
                            <div key={index} className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                              <h4 className="font-bold text-gray-900 mb-2">{miembro.nombre}</h4>
                              <div className="text-sm text-gray-700">
                                <p>{miembro.institucion}</p>
                                <p className="flex items-center gap-2 mt-1">
                                  <Globe className="w-4 h-4" />
                                  {miembro.pais}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Directrices para Envíos */}
              <section id="directrices-envio" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <FileCheck className="w-8 h-8 text-[#0b742f]" />
                      Directrices para autores/as - Envíos
                    </h2>

                    <div className="space-y-8">
                      {/* 1. Envío de manuscritos */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-l-4 border-[#0b742f]">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Mail className="w-6 h-6 text-[#0b742f]" />
                          1. Envío de manuscritos
                        </h3>
                        <div className="space-y-3 text-gray-700">
                          <p>
                            Para publicar en Editorial CENID, envíe su trabajo en formato <strong>Word</strong> al correo:
                          </p>
                          <div className="bg-white p-4 rounded-lg">
                            <a href="mailto:redesdeproduccion@cenid.org" className="text-[#0b742f] font-bold text-lg hover:underline flex items-center gap-2">
                              <Mail className="w-5 h-5" />
                              redesdeproduccion@cenid.org
                            </a>
                          </div>
                          <p className="bg-white p-3 rounded">
                            Se aceptan artículos en <strong>español, inglés y portugués</strong> que sean originales e inéditos.
                          </p>
                        </div>
                      </div>

                      {/* 2. Tipos de contribuciones */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <FileText className="w-6 h-6 text-blue-600" />
                          2. Tipos de contribuciones
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                            <h4 className="font-bold text-blue-900 mb-3">Artículo de investigación</h4>
                            <ul className="space-y-2 text-sm text-gray-700">
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <span>Resultados de investigación completa y original</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <span><strong>Extensión máxima:</strong> 30 cuartillas (incluyendo tablas, figuras y referencias)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <span>Debe incluir contexto teórico y metodología claros</span>
                              </li>
                            </ul>
                          </div>

                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
                            <h4 className="font-bold text-purple-900 mb-3">Ensayo</h4>
                            <ul className="space-y-2 text-sm text-gray-700">
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                <span>Aportación crítica y analítica sobre un tema</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                <span><strong>Extensión máxima:</strong> 20 cuartillas</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                <span>Debe contener referencias bibliográficas</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* 3. Preparación del manuscrito */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <PenTool className="w-6 h-6 text-orange-600" />
                          3. Preparación del manuscrito
                        </h3>

                        {/* Formato general */}
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mb-4">
                          <h4 className="font-bold text-orange-900 mb-3">Formato general</h4>
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                            <li className="flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-orange-600" />
                              <span>Interlineado: <strong>1.5</strong></span>
                            </li>
                            <li className="flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-orange-600" />
                              <span>Fuente: <strong>Times New Roman 12pt</strong></span>
                            </li>
                            <li className="flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-orange-600" />
                              <span>Texto: <strong>Justificado</strong></span>
                            </li>
                            <li className="flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-orange-600" />
                              <span>Márgenes mínimos: <strong>2.5cm</strong></span>
                            </li>
                          </ul>
                        </div>

                        {/* Estructura requerida */}
                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-5">
                          <h4 className="font-bold text-gray-900 mb-3">Estructura requerida</h4>
                          <div className="space-y-3 text-sm text-gray-700">
                            <div className="bg-white p-3 rounded">
                              <p className="font-semibold mb-1">📄 Portada:</p>
                              <ul className="ml-4 space-y-1">
                                <li>• Título en español e inglés (máx. 20 palabras)</li>
                                <li>• Lista de autores con afiliaciones institucionales</li>
                                <li>• Correo electrónico y ORCID de cada autor</li>
                              </ul>
                            </div>

                            <div className="bg-white p-3 rounded">
                              <p className="font-semibold mb-1">📝 Resumen/Abstract (250 palabras máximo):</p>
                              <ul className="ml-4 space-y-1">
                                <li>• Contexto</li>
                                <li>• Objetivos</li>
                                <li>• Métodos</li>
                                <li>• Resultados clave</li>
                                <li>• Conclusiones</li>
                              </ul>
                            </div>

                            <div className="bg-white p-3 rounded">
                              <p className="font-semibold mb-1">📑 Secciones del documento:</p>
                              <ul className="ml-4 space-y-1">
                                <li>• Palabras clave (6 máximo)</li>
                                <li>• Introducción</li>
                                <li>• Materiales y métodos</li>
                                <li>• Resultados</li>
                                <li>• Discusión</li>
                                <li>• Conclusiones</li>
                                <li>• Agradecimientos (opcional)</li>
                                <li>• Referencias (formato APA 7ª edición)</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 4. Tablas y figuras */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Target className="w-6 h-6 text-teal-600" />
                          4. Tablas y figuras
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-5">
                            <h4 className="font-bold text-teal-900 mb-3">📊 Tablas</h4>
                            <ul className="space-y-2 text-sm text-gray-700">
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                                <span>Crear usando la función de tabla de Word</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                                <span>Incluir título descriptivo y numeración</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                                <span>No repetir datos entre tablas y texto</span>
                              </li>
                            </ul>
                          </div>

                          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-5">
                            <h4 className="font-bold text-cyan-900 mb-3">🖼️ Figuras</h4>
                            <ul className="space-y-2 text-sm text-gray-700">
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                                <span>Formato: <strong>.png</strong></span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                                <span>Numeración consecutiva (Figura 1, Figura 2...)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                                <span>Resolución mínima: <strong>300 dpi</strong></span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* 5. Referencias bibliográficas */}
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <BookText className="w-6 h-6 text-indigo-600" />
                          5. Referencias bibliográficas
                        </h3>
                        <p className="text-gray-700 mb-4">Formato <strong>APA (7ª edición)</strong>. Ejemplos:</p>

                        <div className="space-y-3 text-sm">
                          <div className="bg-white p-4 rounded border-l-4 border-indigo-600">
                            <p className="font-semibold text-indigo-900 mb-2">📚 Libro:</p>
                            <p className="text-gray-700 font-mono text-xs">Autor, A. (Año). Título del libro. Editorial.</p>
                          </div>

                          <div className="bg-white p-4 rounded border-l-4 border-indigo-600">
                            <p className="font-semibold text-indigo-900 mb-2">📄 Artículo de revista:</p>
                            <p className="text-gray-700 font-mono text-xs">Autor, A. (Año). Título del artículo. Nombre de la Revista, volumen(número), páginas. DOI</p>
                          </div>

                          <div className="bg-white p-4 rounded border-l-4 border-indigo-600">
                            <p className="font-semibold text-indigo-900 mb-2">🎓 Tesis:</p>
                            <p className="text-gray-700 font-mono text-xs">Autor, A. (Año). Título de la tesis (Tesis de grado). Universidad.</p>
                          </div>
                        </div>
                      </div>

                      {/* 6. Derechos y políticas */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Shield className="w-6 h-6 text-green-600" />
                          6. Derechos y políticas
                        </h3>
                        <div className="space-y-4">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                            <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                              <Globe className="w-5 h-5" />
                              Acceso abierto
                            </h4>
                            <p className="text-sm text-gray-700">
                              Los autores conservan sus derechos y otorgan a la revista el derecho de primera publicación bajo licencia Creative Commons.
                            </p>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                              <Lock className="w-5 h-5" />
                              Privacidad
                            </h4>
                            <p className="text-sm text-gray-700">
                              Los datos personales solo se usarán para fines editoriales.
                            </p>
                          </div>

                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                            <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5" />
                              Cuota de publicación
                            </h4>
                            <p className="text-sm text-gray-700">
                              Se aplica una cuota de publicación una vez aceptado el artículo.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Contacto final */}
                      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg p-6 text-center">
                        <p className="mb-3">Para consultas adicionales, contacte a:</p>
                        <a
                          href="mailto:redesdeproduccion@cenid.org"
                          className="inline-flex items-center gap-2 bg-white text-[#0b742f] px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                        >
                          <Mail className="w-5 h-5" />
                          redesdeproduccion@cenid.org
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Política de Acceso Abierto */}
              <section id="acceso-abierto" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Globe className="w-8 h-8 text-blue-600" />
                      Política de Acceso Abierto
                    </h2>
                    <div className="space-y-4 text-gray-700">
                      <p>
                        Esta revista proporciona un acceso abierto inmediato a su contenido, basado en el principio de que ofrecer al público un acceso libre a las investigaciones ayuda a un mayor intercambio global de conocimiento.
                      </p>
                      <p>
                        Se permite y se anima a los autores a depositar sus trabajos publicados en otros repositorios, ya que de este modo se favorece su circulación, difusión y preservación.
                      </p>
                      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                        <p className="font-semibold text-blue-900">
                          La política de archivo digital que utiliza la revista es con CLOCKSS.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Política de Autoarchivo */}
              <section id="autoarchivo" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Lock className="w-8 h-8 text-[#e87729]" />
                      Política de Autoarchivo
                    </h2>
                    <div className="space-y-4 text-gray-700">
                      <p>
                        El autor puede compartir, difundir y divulgar sus investigaciones publicadas por los medios de comunicación (e.g., redes sociales académicas, repositorios y portales) disponibles en la web.
                      </p>
                      <p>
                        Después de la publicación de los artículos, los autores pueden realizar otro tipo de acuerdos independientes o adicionales para la difusión no exclusiva de la versión del artículo publicado en la presente revista (publicación en libros o repositorios institucionales).
                      </p>
                      <p>
                        Se permite al autor que todas las versiones de su artículo sean depositadas en un repositorio institucional o de otro tipo a elección del autor, siempre que se indique con la referencia respectiva que el trabajo ha sido publicado por primera vez en esta revista.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Tipos de Contribuciones */}
              <section id="tipos-contribuciones" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-purple-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <FileText className="w-8 h-8 text-purple-600" />
                      Tipos de Contribuciones
                    </h2>
                    <div className="mb-6 bg-purple-50 rounded-lg p-4 border-l-4 border-purple-600">
                      <p className="text-gray-800">
                        Editorial CENID publica artículos de investigación que no han sido previamente publicados. Reportan de manera clara y precisa los resultados de una investigación, cuyo propósito es aportar información que contribuya al desarrollo educativo o del área de conocimiento en que se aplica.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {tiposContribuciones.map((tipo, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6 border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all duration-200"
                        >
                          <h3 className="text-xl font-bold text-purple-900 mb-3">{tipo.tipo}</h3>
                          <p className="text-gray-700 mb-3">{tipo.descripcion}</p>
                          <div className="flex items-center gap-2 text-sm">
                            <FileCheck className="w-4 h-4 text-purple-600" />
                            <span className="font-semibold text-purple-700">{tipo.extension}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-500">
                      <p className="text-gray-800">
                        <strong>Nota:</strong> Por excepción se aceptarán artículos de mayor extensión, en cuyo caso habrá un cargo por cuartilla extra. Sólo se aceptarán en serie si al menos se envían dos en forma simultánea.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Preparación de Manuscritos */}
              <section id="preparacion" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <PenTool className="w-8 h-8 text-[#0b742f]" />
                      Preparación de los Manuscritos
                    </h2>
                    <p className="text-gray-700 mb-8">
                      Siga cuidadosamente cada uno de estos pasos para garantizar que su manuscrito cumpla con los estándares de Editorial CENID:
                    </p>
                    <div className="space-y-6">
                      {preparacionPasos.map((paso) => (
                        <div
                          key={paso.numero}
                          className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-l-4 border-[#0b742f]"
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 bg-[#0b742f] text-white rounded-full flex items-center justify-center font-bold text-lg">
                                {paso.numero}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">{paso.titulo}</h3>
                              <ul className="space-y-2">
                                {paso.items.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-gray-700">
                                    <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Organización de Manuscritos */}
              <section id="organizacion" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <List className="w-8 h-8 text-blue-600" />
                      Organización de Manuscritos
                    </h2>
                    <p className="text-gray-700 mb-6">
                      Los manuscritos deben organizarse de la siguiente manera. Las instrucciones para cada elemento aparecen a continuación:
                    </p>
                    <div className="space-y-8">
                      {organizacionSecciones.map((categoria, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-2 border-blue-200">
                          <h3 className="text-2xl font-bold text-blue-900 mb-4">{categoria.categoria}</h3>
                          <div className="space-y-3">
                            {categoria.secciones.map((seccion, sidx) => (
                              <div key={sidx} className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                <h4 className="font-bold text-gray-900 mb-1">{seccion.nombre}</h4>
                                <p className="text-gray-700 text-sm">{seccion.descripcion}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Lista de Comprobación */}
              <section id="comprobacion" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <ClipboardCheck className="w-8 h-8 text-[#e87729]" />
                      Lista de Comprobación de Preparación de Envíos
                    </h2>
                    <p className="text-gray-700 mb-6">
                      Antes de enviar su manuscrito, verifique que cumple con todos estos requisitos:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {listaComprobacion.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 hover:border-[#e87729] hover:shadow-md transition-all duration-200"
                        >
                          <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Formato de Evaluación */}
              <section id="evaluacion" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-purple-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <ClipboardCheck className="w-8 h-8 text-purple-600" />
                      Formato de Evaluación
                    </h2>
                    <p className="text-gray-700 mb-8">
                      A continuación, se listan los aspectos más importantes que los autores deben revisar para evaluar su trabajo. Los árbitros utilizarán estos criterios para la evaluación de los manuscritos:
                    </p>
                    <div className="space-y-6">
                      {formatoEvaluacion.map((item, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-6 border-l-4 border-purple-600 hover:shadow-lg transition-shadow duration-200"
                        >
                          <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                            <Target className="w-6 h-6" />
                            {item.seccion}
                          </h3>

                          {item.descripcion && (
                            <p className="text-gray-800 mb-4 font-medium">{item.descripcion}</p>
                          )}

                          {item.criterios.length > 0 && (
                            <div className="space-y-3">
                              {item.criterios.map((criterio, cidx) => (
                                <div key={cidx} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-700">{criterio}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {item.nota && (
                            <div className="mt-4 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                              <p className="text-sm text-gray-700 italic">
                                <strong>Nota:</strong> {item.nota}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6 border-l-4 border-blue-600">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <BookText className="w-6 h-6 text-blue-600" />
                        Proceso de Arbitraje
                      </h3>
                      <div className="space-y-3 text-gray-700">
                        <p>
                          En el referéndum participarán <strong>dos científicos especialistas</strong> en el tema, cuyas opiniones serán consideradas para la aceptación del trabajo.
                        </p>
                        <p>
                          En caso de divergencia entre los árbitros, el artículo y las opiniones serán presentados a un <strong>tercer revisor</strong>.
                        </p>
                        <p>
                          Cuando el trabajo haya sido aceptado, el manuscrito con los dictámenes de los revisores se enviará a los autores para realizar las modificaciones pertinentes.
                        </p>
                        <p className="font-semibold text-amber-700">
                          Si la versión corregida no fuera devuelta en los <strong>seis meses posteriores</strong> a la recepción de la revisión se considerará que el trabajo ha sido retirado para su publicación.
                        </p>
                        <p>
                          La decisión final sobre la aceptación de un trabajo corresponderá al propio Comité Editorial, tomando en cuenta las opiniones de los revisores.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-6 border-l-4 border-yellow-500">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Políticas Adicionales</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span>No se aceptarán trabajos que, pudiendo integrarse como unidad, sean presentados por separado en forma de pequeñas contribuciones o notas numeradas.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span>No serán aceptadas contribuciones preliminares o inconclusas, que sean factibles de terminar a mediano o corto plazos.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span>Todo trabajo rechazado para su publicación no será aceptado con posterioridad.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span>El orden de publicación atenderá a las fechas de recepción y aceptación del trabajo.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Ética de la Investigación y la Publicación */}
              <section id="etica" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-red-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Heart className="w-8 h-8 text-red-600" />
                      Ética de la investigación y la publicación
                    </h2>

                    <div className="space-y-8">
                      {/* Introducción */}
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg border-l-4 border-red-600">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Shield className="w-6 h-6 text-red-600" />
                          Ética de la investigación
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          Editorial CENID busca alcanzar la excelencia científica su código ético se apoya en el <strong>Comité de Ética de Publicaciones (COPE)</strong>, se encuentran dirigidos a los editores, revisores y autores.
                        </p>
                      </div>

                      {/* Compromisos de los autores */}
                      <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <UserCircle className="w-7 h-7 text-blue-600" />
                          Compromisos de los autores
                        </h3>
                        <div className="space-y-4">
                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <FileSearch className="w-5 h-5 text-blue-600" />
                              Originalidad y plagio
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los autores de manuscritos postulados aseguran que el trabajo es original, que no contiene partes de otros autores o de otros fragmentos de trabajos ya publicados por los autores. Además, confirman la veracidad de los datos, esto es, que no se han alterado los datos empíricos para verificar hipótesis.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-blue-600" />
                              Publicaciones múltiples y/o repetitivas
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              El autor no debe publicar artículos en los que se repitan los mismos resultados en más de una revista científica. La propuesta simultánea de la misma contribución a múltiples revistas científicas es considerada éticamente incorrecta y reprobable.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <BookText className="w-5 h-5 text-blue-600" />
                              Lista de fuentes
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              El autor debe proporcionar siempre la correcta indicación de las fuentes y los aportes mencionados en el artículo.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Users className="w-5 h-5 text-blue-600" />
                              Autoría
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              En cuanto a la autoría del manuscrito, los autores garantizan la inclusión de aquellas personas que han hecho una contribución científica e intelectual significativa en la conceptualización y la planificación del trabajo como en la interpretación de los resultados y en la redacción del mismo. Al mismo tiempo se han jerarquizado los autores conforme a su nivel de responsabilidad e implicación.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Lock className="w-5 h-5 text-blue-600" />
                              Acceso y retención
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Si los editores lo consideran apropiado, los autores de los artículos deben poner a disposición también las fuentes o los datos en que se basa la investigación, que puede conservarse durante un período razonable de tiempo después de la publicación y posiblemente hacerse accesible.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-blue-600" />
                              Conflicto de intereses y divulgación
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Todos los autores están obligados a declarar explícitamente que no hay conflictos de intereses que puedan haber influido en los resultados obtenidos o las interpretaciones propuestas. Los autores también deben indicar cualquier financiación de agencias y/o de proyectos de los que surge el artículo de la investigación.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-blue-600" />
                              Errores en los artículos publicados
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Cuando un autor identifica en su artículo un importante error o una inexactitud, deberá inmediatamente informar a los editores de la revista y proporcionarles toda la información necesaria para listar las correcciones pertinentes en la parte inferior del mismo artículo.
                            </p>
                          </div>

                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                              Responsabilidad
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Todos los autores aceptan la responsabilidad de lo que se ha escrito. Los autores se comprometen también a que se ha realizado una revisión de la literatura científica más actual y relevante del tema analizado, teniendo presente de forma plural las diferentes corrientes del conocimiento.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Compromisos de los revisores */}
                      <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <Eye className="w-7 h-7 text-purple-600" />
                          Compromisos de los revisores
                        </h3>
                        <div className="space-y-4">
                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Target className="w-5 h-5 text-purple-600" />
                              Contribución a la decisión editorial
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              La revisión por pares es un procedimiento que ayuda a los editores para tomar decisiones sobre los artículos propuestos y también permite al autor mejorar la contribución enviada para su publicación. Los revisores asumen el compromiso de realizar una revisión crítica, honesta, constructiva y sin sesgo, tanto de la calidad científica como de la calidad literaria del escrito en el campo de sus conocimientos y habilidades.
                            </p>
                          </div>

                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Clock className="w-5 h-5 text-purple-600" />
                              Respeto de los tiempos
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              El revisor que no sienta competente en la temática a revisar o que no pueda terminar la evaluación en el tiempo programado notificará de inmediato a los editores. Los revisores se comprometen a evaluar los trabajos en el tiempo menor posible para respetar los plazos de entrega.
                            </p>
                          </div>

                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Lock className="w-5 h-5 text-purple-600" />
                              Confidencialidad
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Cada manuscrito asignado debe ser considerado como confidencial. Por lo tanto, estos textos no se deben discutir con otras personas sin el consentimiento expreso de los editores.
                            </p>
                          </div>

                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Scale className="w-5 h-5 text-purple-600" />
                              Objetividad
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              La revisión por pares se realizará de manera objetiva. No se considera adecuado ningún juicio personal sobre los autores de las contribuciones. Los revisores están obligados a dar razones suficientes para sus valoraciones. Los revisores entregarán un informe crítico completo con referencias adecuadas según protocolo de revisiones de RIDE y las normativas públicas para los revisores; especialmente, si se propone que el trabajo sea rechazado. Están obligados a advertir a los editores si partes sustanciales del trabajo ya han sido publicadas o están bajo revisión para otra publicación.
                            </p>
                          </div>

                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Search className="w-5 h-5 text-purple-600" />
                              Visualización de texto
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los revisores se comprometen a indicar con precisión las referencias bibliográficas de obras fundamentales posiblemente olvidadas por el autor. El revisor también debe informar a los editores de cualquier similitud o solapamientos del manuscrito con otros trabajos publicados.
                            </p>
                          </div>

                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-purple-600" />
                              Conflicto de intereses y divulgación
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Información confidencial o información obtenida durante el proceso de revisión por pares debe considerarse confidencial y no puede utilizarse para propósitos personales. Los revisores solo revisan un manuscrito si no existen conflictos de interés.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Compromisos de los editores */}
                      <div className="bg-white border-2 border-green-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <BookOpen className="w-7 h-7 text-green-600" />
                          Compromisos de los editores
                        </h3>
                        <div className="space-y-4">
                          <div className="bg-green-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              Decisión de publicación
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los editores garantizarán la selección de los revisores más cualificados y especialistas científicamente para emitir una apreciación crítica y experta del trabajo, con los menores sesgos posibles.
                            </p>
                          </div>

                          <div className="bg-green-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Heart className="w-5 h-5 text-green-600" />
                              Honestidad
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los editores evalúan los artículos enviados para su publicación sólo sobre la base del mérito científico de los contenidos, sin discriminación de raza, género, orientación sexual, religión, origen étnico, nacionalidad, opinión política de los autores.
                            </p>
                          </div>

                          <div className="bg-green-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Lock className="w-5 h-5 text-green-600" />
                              Confidencialidad
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los editores y los miembros del grupo de trabajo se comprometen a no divulgar la información relativa a los artículos enviados para su publicación a otras personas que no sean autores, revisores y editores. Los editores y el Comité Editorial se comprometen a la confidencialidad de los manuscritos, sus autores y revisores, de forma que el anonimato preserve la integridad intelectual de todo el proceso.
                            </p>
                          </div>

                          <div className="bg-green-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-green-600" />
                              Conflicto de intereses y divulgación
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los editores se comprometen a no utilizar en sus investigaciones contenidos de los artículos enviados para su publicación sin el consentimiento por escrito del autor.
                            </p>
                          </div>

                          <div className="bg-green-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                              <Clock className="w-5 h-5 text-green-600" />
                              Respeto de los tiempos
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                              Los editores son responsables máximos del cumplimiento de los límites de tiempo para las revisiones y la publicación de los trabajos aceptados, para asegurar una rápida difusión de sus resultados. Se comprometen fehacientemente a cumplir los tiempos publicados (máximo de 10 días en la estimación/desestimación desde la recepción del manuscrito en la Plataforma de Revisión) y máximo 120 días desde el inicio del proceso de revisión científica por expertos). Asimismo, los trabajos no permanecerán aceptados sin publicarse, RIDE adopta la publicación continua.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Código ético COPE */}
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border-2 border-indigo-300">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Award className="w-7 h-7 text-indigo-600" />
                          Código ético
                        </h3>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Editorial CENID se adhiere a las normas éticas de <strong>COPE: Committee on Publication Ethics</strong>
                        </p>
                        <div className="bg-white p-4 rounded-lg text-center">
                          <p className="text-sm text-gray-600 italic">
                            Logo COPE - Committee on Publication Ethics
                          </p>
                        </div>
                      </div>

                      {/* Investigación con sujetos humanos */}
                      <div className="bg-white border-2 border-red-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <UserCheck className="w-7 h-7 text-red-600" />
                          Investigación que involucra sujetos humanos
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Al informar sobre investigaciones que involucren sujetos humanos, material humano, tejidos humanos o datos humanos, los autores deben declarar que las investigaciones se llevaron a cabo siguiendo las reglas de la <strong>Declaración de Helsinki de 1975</strong> (<a href="https://www.wma.net/what-we-do/medical-ethics/declaration-of-helsinki/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.wma.net/what-we-do/medical-ethics/declaration-of-helsinki/</a>), revisado en 2013. Según el punto 23 de esta Declaración, se debe obtener la aprobación de la junta de revisión institucional local (IRB) u otro comité de ética apropiado antes de emprender. La investigación para confirmar que el estudio cumple con las directrices nacionales e internacionales. Como mínimo, en la sección "Declaración de la Junta de Revisión Institucional" del artículo se debe incluir una declaración que incluya el código de identificación del proyecto, la fecha de aprobación y el nombre del comité de ética o la junta de revisión institucional.
                          </p>

                          <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                            <p className="font-semibold mb-2">Ejemplo de declaración ética:</p>
                            <p className="italic">
                              "Todos los sujetos dieron su consentimiento informado para su inclusión antes de participar en el estudio. El estudio se realizó de acuerdo con la Declaración de Helsinki y el protocolo fue aprobado por el Comité de Ética de XXX (Código de identificación del proyecto)."
                            </p>
                          </div>

                          <p className="leading-relaxed">
                            Para estudios no intervencionistas (por ejemplo, encuestas, cuestionarios, investigaciones en redes sociales), todos los participantes deben estar completamente informados si se garantiza el anonimato, por qué se realiza la investigación, cómo se utilizarán sus datos y si existe algún riesgo asociado. Como ocurre con todas las investigaciones que involucran seres humanos, se debe obtener la aprobación ética de un comité de ética apropiado antes de realizar el estudio.
                          </p>

                          <p className="leading-relaxed">
                            Se debe obtener un <strong>consentimiento informado por escrito</strong> para la publicación de los pacientes participantes. Los datos relacionados con los participantes individuales deben describirse en detalle, pero no es necesario incluir información privada que identifique a los participantes, a menos que los materiales identificables sean relevantes para la investigación.
                          </p>
                        </div>
                      </div>

                      {/* Animales en investigación */}
                      <div className="bg-white border-2 border-amber-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <TestTube className="w-7 h-7 text-amber-600" />
                          Directrices éticas para el uso de animales en investigación
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Los editores exigirán que los beneficios potencialmente derivados de cualquier investigación que cause daño a los animales sean significativos en relación con cualquier costo soportado por los animales y, que es poco probable, que los procedimientos seguidos ofendan a la mayoría de los lectores.
                          </p>

                          <div className="bg-amber-50 p-5 rounded-lg">
                            <h4 className="font-bold text-lg text-gray-900 mb-3">Las "3R" comúnmente aceptadas:</h4>
                            <ul className="space-y-2">
                              <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span><strong>Reemplazo</strong> de animales por alternativas siempre que sea posible</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span><strong>Reducción</strong> del número de animales utilizados</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span><strong>Refinamiento</strong> de las condiciones y procedimientos experimentales para minimizar el daño a los animales</span>
                              </li>
                            </ul>
                          </div>

                          <p className="leading-relaxed">
                            Editorial CENID respalda las pautas de <strong>ARRIVE</strong> (<a href="https://arriveguidelines.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">arriveguidelines.org/</a>) para informar experimentos con animales vivos.
                          </p>
                        </div>
                      </div>

                      {/* Líneas celulares */}
                      <div className="bg-white border-2 border-cyan-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Microscope className="w-7 h-7 text-cyan-600" />
                          Investigación que involucra líneas celulares
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Las secciones de métodos para envíos de informes sobre investigaciones con líneas celulares deben indicar el origen de las líneas celulares. Para las líneas celulares establecidas se debe indicar la procedencia y también se deben dar referencias a un artículo publicado o a una fuente comercial.
                          </p>

                          <div className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-500">
                            <p className="font-semibold mb-2">Ejemplo de Declaraciones Éticas:</p>
                            <p className="italic">
                              La línea celular HCT116 se obtuvo de XXXX. La línea celular MLH1+ fue proporcionada por XXXXX, Ltd. La línea celular DLD-1 se obtuvo del Dr. XXXX. Los plásmidos indicadores DR-GFP y SA-GFP se obtuvieron del Dr. XXX y el vector de expresión Rad51K133A se obtuvo del Dr. XXXX.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Investigación con plantas */}
                      <div className="bg-white border-2 border-lime-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Leaf className="w-7 h-7 text-lime-600" />
                          Investigación que involucra plantas
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            La investigación experimental con plantas (ya sean cultivadas o silvestres), incluida la recolección de material vegetal, debe cumplir con directrices institucionales, nacionales o internacionales. Recomendamos que los autores cumplan con el <a href="http://www.cbd.int/convention/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Convenio sobre la Diversidad Biológica</a> y el <a href="http://www.cites.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Convenio sobre el Comercio de Especies Amenazadas de Fauna y Flora Silvestres</a>.
                          </p>

                          <div className="bg-lime-50 p-4 rounded-lg border-l-4 border-lime-500">
                            <p className="font-semibold mb-2">Ejemplo de Declaraciones Éticas:</p>
                            <p className="italic">
                              En este estudio se utilizaron plantas de Chamaemelum nobile. Los cultivares fueron seleccionados de 'Agromatica' (XXX Company, City, Country) fueron proporcionados amablemente por el Dr. XXX (XXX Institute, City, Country).
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Registro de ensayos clínicos */}
                      <div className="bg-white border-2 border-teal-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Stethoscope className="w-7 h-7 text-teal-600" />
                          Registro de ensayos clínicos
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <h4 className="font-bold text-lg text-gray-900">Registro</h4>
                          <p className="leading-relaxed">
                            Editorial CENID sigue las pautas del <strong>Comité Internacional de Editores de Revistas Médicas (ICMJE)</strong> que requieren y recomiendan el registro de ensayos clínicos en un registro de ensayos públicos en el momento de la inscripción del primer paciente o antes como condición para su consideración para la publicación.
                          </p>

                          <div className="bg-teal-50 p-4 rounded-lg">
                            <p className="leading-relaxed mb-3">
                              Se recomienda encarecidamente a los autores que preinscriban los ensayos clínicos en un registro internacional de ensayos clínicos. Las bases de datos adecuadas incluyen:
                            </p>
                            <ul className="space-y-2">
                              <li className="flex items-start gap-3">
                                <ArrowRight className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                <a href="https://clinicaltrials.gov/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Clinicaltrials.gov</a>
                              </li>
                              <li className="flex items-start gap-3">
                                <ArrowRight className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                <a href="https://www.clinicaltrialsregister.eu/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Registro de Ensayos Clínicos de la UE</a>
                              </li>
                              <li className="flex items-start gap-3">
                                <ArrowRight className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                <a href="https://www.who.int/clinical-trials-registry-platform" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Plataforma de Registro Internacional de Ensayos Clínicos de la OMS</a>
                              </li>
                            </ul>
                          </div>

                          <h4 className="font-bold text-lg text-gray-900 mt-6">Declaración CONSORT</h4>
                          <p className="leading-relaxed">
                            Editorial CENID requiere una lista de verificación CONSORT 2010 completa y un diagrama de flujo como condición de presentación al informar los resultados de un ensayo aleatorio. Las plantillas para estos se pueden encontrar en el sitio web de CONSORT (<a href="http://www.consort-statement.org" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">http://www.consort-statement.org</a>).
                          </p>
                        </div>
                      </div>

                      {/* Investigación de doble uso */}
                      <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <FlaskConical className="w-7 h-7 text-orange-600" />
                          Investigación de doble uso de interés
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Editorial CENID sigue el marco práctico definido en <strong>Guía para editores: investigación, auditoría y evaluaciones</strong> de servicios e introducido por el Comité de Ética en Publicaciones (COPE). Las investigaciones que podrían representar una amenaza significativa, con amplias consecuencias potenciales para la salud pública o la seguridad nacional, deben indicarse claramente en el manuscrito.
                          </p>
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <p className="leading-relaxed">
                              Las posibles áreas de preocupación incluyen, entre otras: <strong>bioseguridad, amenazas nucleares y químicas, investigación con fines o aplicaciones militares</strong>, etc. Para que estos manuscritos sean considerados para revisión por pares, los beneficios para el público general o la salud pública deben superar los riesgos.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Sexo y género en la investigación */}
                      <div className="bg-white border-2 border-pink-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Users className="w-7 h-7 text-pink-600" />
                          Sexo y género en la investigación
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Alentamos a nuestros autores a seguir las "<a href="https://researchintegrityjournal.biomedcentral.com/articles/10.1186/s41073-016-0007-6" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">directrices sobre sexo y equidad de género en la investigación (SAGER)</a>" e incluir consideraciones de sexo y género cuando sea relevante. Los autores deben utilizar los términos <strong>sexo</strong> (atributo biológico) y <strong>género</strong> (modelado por circunstancias sociales y culturales) con cuidado para evitar confundir ambos términos.
                          </p>
                        </div>
                      </div>

                      {/* Fronteras y Territorios */}
                      <div className="bg-white border-2 border-slate-200 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Map className="w-7 h-7 text-slate-600" />
                          Fronteras y Territorios
                        </h3>
                        <div className="space-y-4 text-gray-700">
                          <p className="leading-relaxed">
                            Las posibles disputas sobre fronteras y territorios pueden tener particular relevancia para los autores al describir su investigación o en la dirección de correspondencia del autor o editor, y deben respetarse. Las decisiones de contenido son una cuestión editorial y cuando existe una disputa o queja potencial o percibida, el equipo editorial intentará encontrar una resolución que satisfaga a las partes involucradas.
                          </p>
                          <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-500">
                            <p className="font-semibold">
                              Editorial CENID se mantiene neutral con respecto a reclamos jurisdiccionales en mapas publicados y afiliaciones institucionales.
                            </p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </section>

              {/* Comité Evaluador - Revisores */}
              <section id="revisores" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-indigo-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <GraduationCap className="w-8 h-8 text-indigo-600" />
                      Comité Evaluador - Revisores Editorial CENID
                    </h2>

                    <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border-l-4 border-indigo-600">
                      <p className="text-gray-700 leading-relaxed">
                        Nuestro comité evaluador está conformado por <strong>{revisores.length} distinguidos académicos e investigadores</strong> de reconocidas instituciones a nivel internacional, quienes garantizan la calidad científica y académica de las publicaciones a través del proceso de revisión por pares.
                      </p>
                    </div>

                    {/* Grid de Revisores */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {revisores.map((revisor, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all duration-200 hover:border-indigo-300"
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <div className="bg-indigo-100 p-2 rounded-lg flex-shrink-0">
                              <GraduationCap className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1">
                                {revisor.nombre}
                              </h3>
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex items-start gap-2">
                              <BookOpen className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                              <p className="text-gray-700 leading-snug text-xs">
                                {revisor.institucion}
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <p className="text-gray-600 text-xs font-medium">
                                {revisor.pais}
                              </p>
                            </div>

                            <div className="pt-2 mt-2 border-t border-gray-200">
                              <a
                                href={`https://orcid.org/${revisor.orcid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors text-xs font-mono"
                              >
                                <Award className="w-4 h-4" />
                                <span>ORCID: {revisor.orcid}</span>
                                <ExternalLink className="w-3 h-3 ml-auto" />
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Estadísticas */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-blue-600 mb-2">{revisores.length}</div>
                        <p className="text-gray-700 font-medium">Total Revisores</p>
                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-purple-600 mb-2">
                          {[...new Set(revisores.map(r => r.pais))].length}
                        </div>
                        <p className="text-gray-700 font-medium">Países Representados</p>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {revisores.length}
                        </div>
                        <p className="text-gray-700 font-medium">Investigadores ORCID</p>
                      </div>
                    </div>

                    {/* Nota informativa */}
                    <div className="mt-6 bg-indigo-50 border border-indigo-200 p-5 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Shield className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700 leading-relaxed">
                          <p className="mb-2">
                            <strong>Todos nuestros revisores cuentan con identificación ORCID</strong>, garantizando la trazabilidad y reconocimiento de sus contribuciones académicas.
                          </p>
                          <p>
                            El proceso de revisión por pares es <strong>doble ciego</strong>, asegurando la objetividad e imparcialidad en la evaluación de los manuscritos.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Nota de Copyright */}
              <section id="copyright" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <FileSignature className="w-8 h-8 text-[#e87729]" />
                      Nota de Copyright
                    </h2>

                    <div className="space-y-6">
                      {/* Creative Commons */}
                      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#e87729]">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          <strong>Editorial CENID</strong> se apega al sistema de derechos de autor propuesto por <strong>Creative Commons</strong>, con la cual los contenidos que se publican en la Revista se tienen sólo "algunos derechos reservados", bajo la licencia <strong>Atribución 4.0 Internacional (CC BY 4.0)</strong>. Por tanto, la editorial CENID también trabaja en acuerdo con el proyecto <strong>Science Commons</strong>, derivado de Creative Commons, dedicado específicamente al campo de la ciencia.
                        </p>
                      </div>

                      {/* Responsabilidad Legal */}
                      <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                          <p className="text-gray-700 leading-relaxed">
                            Es importante aclarar que ni el Editor, ni la Editorial del Centro de Estudios e Investigaciones para el Desarrollo Docente, a través del CENID A.C., serán legalmente responsables si se presenta alguna demanda de indemnización por el trabajo aquí publicado. <strong>El único responsable es el autor</strong> a quien se dirige la correspondencia.
                          </p>
                        </div>
                      </div>

                      {/* Licencia Creative Commons */}
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <Award className="w-6 h-6 text-green-600" />
                          <h3 className="text-xl font-bold text-gray-900">Esta obra está bajo una licencia de Creative Commons</h3>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <h4 className="font-bold text-lg text-gray-900 mb-3">Eres libre de:</h4>
                            <ul className="space-y-2">
                              <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700"><strong>Compartir</strong> — copiar y redistribuir el material en cualquier medio o formato.</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700"><strong>Adaptar</strong> — mezclar, transformar y construir sobre el material para cualquier fin, incluso comercial.</span>
                              </li>
                            </ul>
                          </div>

                          <div>
                            <h4 className="font-bold text-lg text-gray-900 mb-3">Bajo los siguientes términos:</h4>
                            <div className="space-y-3">
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <p className="text-gray-700">
                                  <strong>1. Atribución:</strong> debe otorgar el crédito adecuado, proporcionar un enlace a la licencia e indicar si se realizaron cambios. Puede hacerlo de cualquier manera razonable, pero no de ninguna manera que sugiera que el licenciante lo respalda a usted o su uso.
                                </p>
                              </div>
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <p className="text-gray-700">
                                  <strong>2. No hay restricciones adicionales:</strong> No puede aplicar términos legales ni medidas tecnológicas que restrinjan legalmente a otras a hacer cualquier uso permitido por la licencia.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Compromiso Editorial */}
                      <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                        <p className="text-gray-700 leading-relaxed">
                          El cuerpo editorial de la editorial asumirá el compromiso de notificar oportunamente a los autores de los artículos sobre cualquier cambio en términos de su ubicación en el World Wide Web (cambio de dirección o de conexiones para identificar el artículo).
                        </p>
                      </div>

                      {/* Derechos de Autor */}
                      <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-600">
                        <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-purple-600" />
                          Derechos de autor
                        </h4>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Los autores conservan los derechos de autoría y cederán a la revista los derechos de la publicación, edición, reproducción, distribución, exhibición y comunicación a nivel nacional e internacional en las diferentes bases de datos, repositorios y portales.
                        </p>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-gray-700 leading-relaxed">
                            <strong>La Editorial del Centro de Estudios e Investigaciones para el Desarrollo Docente</strong> utiliza programas especializados para verificar la correcta citación en el artículo / capítulo de libro. Si se detecta más del <strong className="text-red-600">10% de malas prácticas</strong> en la forma de citar se rechazará el registro del artículo. De detectar más de <strong className="text-red-600">50%</strong> se informará al director o representante de la institución a la cual se encuentra adscrito el autor principal.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Deslinde de Responsabilidades y Privacidad */}
              <section id="privacidad" className="scroll-mt-32">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
                  <div className="p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Scale className="w-8 h-8 text-blue-600" />
                      Deslinde de Responsabilidades y Privacidad
                    </h2>

                    <div className="space-y-6">
                      {/* Deslinde de Responsabilidades */}
                      <div>
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border-l-4 border-red-500 mb-4">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Shield className="w-6 h-6 text-red-600" />
                            Deslinde de responsabilidades
                          </h3>
                          <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-gray-700 leading-relaxed">
                                  <strong>La Editorial del Centro de Estudios e Investigaciones para el Desarrollo Docente no se hace responsable</strong> de las ideas y opiniones expresadas por los autores de los artículos o colaboraciones.
                                </p>
                              </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                              <div className="flex items-start gap-3">
                                <Globe className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-gray-700 leading-relaxed">
                                  <strong>La Editorial del Centro de Estudios e Investigaciones para el Desarrollo Docente no tienen bajo su control ni se hace responsable</strong> de los enlaces e hipertexto que, en algunos artículos, posibiliten el acceso a prestaciones y servicios ofrecidos por terceros en otros sitios web.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Declaración de Privacidad */}
                      <div>
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-lg border-l-4 border-blue-500">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <UserCheck className="w-6 h-6 text-blue-600" />
                            Declaración de Privacidad
                          </h3>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <p className="text-gray-700 leading-relaxed">
                                Los nombres y las direcciones de correo electrónico introducidos en esta revista se usarán <strong>exclusivamente para los fines establecidos en ella</strong> y no se proporcionarán a terceros o para su uso con otros fines.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contacto */}
              <section id="contacto" className="scroll-mt-32">
                <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg shadow-xl p-8">
                  <div className="text-center">
                    <h2 className="text-3xl font-bold mb-4 flex items-center justify-center gap-3">
                      <Mail className="w-8 h-8" />
                      Contacto para Envíos
                    </h2>
                    <p className="text-xl mb-6">
                      Para enviar su trabajo o solicitar información adicional
                    </p>
                    <a
                      href="mailto:redesdeproduccion@cenid.org"
                      className="inline-flex items-center gap-3 bg-white text-[#0b742f] px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      <Mail className="w-6 h-6" />
                      redesdeproduccion@cenid.org
                    </a>
                  </div>
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProcesoEditorial;
